import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import Modal from "react-modal";
// import { GatsbyImage } from "gatsby-plugin-image";
import { PrismicRichText } from "@prismicio/react";
import { RemoveScroll } from "react-remove-scroll";
import { useMeasure } from "react-use";
import { useSwipeable } from "react-swipeable";

// Fullpage
import ReactFullpage from "@fullpage/react-fullpage";

// Components
import { GalleryImage } from "./image";

// Icons
import { CloseIcon } from "../icons/close-icons";
import { ArtworkEnquiryForm } from "../forms/artwork-enquiry";

const FullPageContainer = styled.div`
  color: #000;

  & .fp-watermark {
    display: none;
  }

  & .fp-table {
    justify-content: flex-start;
  }

  & .fp-arrow {
    position: absolute;
    top: 0;
    bottom: 0;

    height: 100%;
    width: 50%;

    z-index: 10;

    margin: 0;
    transform: unset;
    user-select: unset;

    &.fp-prev,
    &.fp-next {
      border-width: unset;
      border-color: unset;
      border: 0;
    }

    &.fp-prev {
      left: 0;
      cursor: w-resize;

      cursor: url(/icons/exhibition-arrow-previous@1x.png) 9 9, w-resize !important;
      cursor: -webkit-image-set(
            url(/icons/exhibition-arrow-previous@1x.png) 1x,
            url(/icons/exhibition-arrow-previous@2x.png) 2x
          )
          9 9,
        w-resize !important;
    }

    &.fp-next {
      right: 0;
      cursor: e-resize;

      cursor: url(/icons/exhibition-arrow-next@1x.png) 9 9, e-resize !important;
      cursor: -webkit-image-set(
            url(/icons/exhibition-arrow-next@1x.png) 1x,
            url(/icons/exhibition-arrow-next@2x.png) 2x
          )
          9 9,
        e-resize !important;
    }
  }

  & .fp-slidesNav {
    display: none;

    bottom: calc(${(props) => props.minHeight}px + 20px + 20px) !important;
    height: 2px;

    & ul {
      display: grid;

      height: 2px;

      & li {
        width: 100%;

        height: 2px;
        margin: 0;

        & a {
          & span {
            width: 100%;
            height: 2px;
            display: block;

            margin: 0;
            border-radius: 0;
            position: relative;
            left: unset;
            top: unset;

            margin: 0;
            border-radius: 0;

            background-color: #eee;

            &.fp-sr-only {
              display: none;
            }
          }

          &.active {
            & span {
              width: 100%;
              height: 2px;
              display: block;

              margin: 0;
              border-radius: 0;
              position: relative;
              left: unset;
              top: unset;

              margin: 0;
              border-radius: 0;

              background-color: #000;
            }
          }
        }
      }
    }
  }

  @media (max-width: 860px) {
    & .fp-slidesNav {
      bottom: calc(${(props) => props.minHeight}px + 20px + 20px) !important;
    }
  }
`;

const SingleSlide = styled.div`
  position: relative;
  height: 100svh;
  padding-bottom: ${(props) => props.captionHeight || 71}px;

  /* height: calc(${(props) => props.height}px - 71px) !important; */
  overflow: hidden;

  & .gatsby-image-wrapper {
    height: calc(
      ${(props) => props.height}px - ${(props) => props.captionHeight || 71}px -
        40px
    ) !important;

    & img {
      height: calc(
        ${(props) => props.height}px - ${(props) => props.captionHeight || 71}px -
          40px
      ) !important;
      object-fit: contain !important;
    }
  }

  @media (max-width: 860px) {
    padding-bottom: ${(props) => props.captionHeight || 82}px;

    /* height: calc(${(props) => props.height}px - 82px) !important; */

    & .gatsby-image-wrapper {
      height: calc(
        ${(props) => props.height}px - ${(props) => props.captionHeight || 82}px -
          40px
      ) !important;

      & img {
        height: calc(
          ${(props) => props.height}px -
            ${(props) => props.captionHeight || 82}px - 40px
        ) !important;
      }
    }

    &.portrait-image {
      & .gatsby-image-wrapper {
        & img {
          object-fit: contain !important;
        }
      }
    }
  }
`;

const SlideshowContainer = styled.div`
  position: relative;
  overflow: hidden;

  & .multiple-images-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  & .gatsby-image-wrapper-constrained {
    width: 100%;
  }

  /* & .fp-slidesNav {
    & ul {
      grid-template-columns: repeat(${(props) => props.numberOfSlides}, 1fr);
    }
  } */

  &.active.fp-completely {
    & .fp-slidesNav {
      display: block;
    }
  }
`;

const Caption = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;

  padding: 20px 30px;
  min-height: 71px;

  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;

  background-color: #eeeeee;

  text-align: left;

  & .progress-bar {
    height: 1px;
    width: 100%;
    background-color: #000;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }

  & p {
    margin: 0;
    white-space: pre-wrap;
  }

  & br {
    display: none;
  }

  & button {
    transition: 250ms color ease;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        color: #999999;
      }
    }
  }

  & .column {
    &.left {
      text-align: left;
    }

    &.middle {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    }

    &.right {
      text-align: right;
    }
  }

  @media (max-width: 860px) {
    font-size: 15px;
    line-height: 21px;

    padding: 20px 10px;
    min-height: 82px;

    text-align: center;

    & br {
      display: block;
    }

    flex-direction: column;
    gap: 1em;

    & .column {
      &.middle {
        flex-direction: column;
      }

      &.right {
        text-align: center;
      }
    }
  }

  @media (max-width: 600px) {
    grid-template-columns: 50px 1fr 50px;
    grid-column-gap: 10px;
  }
`;

const NextArtworkArrow = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;

  height: 50%;
  width: 100%;

  z-index: 10;

  cursor: s-resize;

  cursor: url(/icons/arrow-down@1x.png) 9 9, s-resize !important;
  cursor: -webkit-image-set(
        url(/icons/arrow-down@1x.png) 1x,
        url(/icons/arrow-down@2x.png) 2x
      )
      9 9,
    s-resize !important;

  &.active {
    display: block;
  }

  &.hidden {
    display: none;
  }

  @media (hover: none) and (pointer: coarse) {
    /* Hide on mobile - MobileSwipeArea handles touch */
    display: none !important;
  }
`;

const PreviousArtworkArrow = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;

  height: 50%;
  width: 100%;

  z-index: 10;

  cursor: n-resize;

  cursor: url(/icons/arrow-up@1x.png) 9 9, n-resize !important;
  cursor: -webkit-image-set(
        url(/icons/arrow-up@1x.png) 1x,
        url(/icons/arrow-up@2x.png) 2x
      )
      9 9,
    n-resize !important;

  &.active {
    display: block;
  }

  &.hidden {
    display: none;
  }

  @media (hover: none) and (pointer: coarse) {
    /* Hide on mobile - MobileSwipeArea handles touch */
    display: none !important;
  }
`;

const CloseButton = styled.button`
  position: fixed;
  top: 0;
  right: 0;

  padding: 30px;

  z-index: 100;

  & path {
    transition: 250ms stroke ease;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      & svg {
        & path {
          stroke: #999999;
        }
      }
    }
  }

  @media (max-width: 600px) {
    padding: 10px;

    & svg {
      width: 12px;
      height: auto;
    }
  }
`;

const MobileSwipeArea = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 5;
  background: transparent;
  touch-action: none;
  display: none;

  @media (hover: none) and (pointer: coarse) {
    display: block;
  }
`;

// const Overlay = styled.div`
//   position: absolute;
//   top: 0;
//   left: 0;
//   bottom: 0;
//   right: 0;

//   /* cursor: ${(props) => props.cursor}; */
//   z-index: 9;

//   /* z-index: ${(props) => props.zIndex}; */
//   /* opacity: 0; */
//   /* visibility: hidden; */
// `;

Modal.setAppElement("#___gatsby");

const SlideshowArtworksContent = ({
  artworks,
  staticWindowHeight,
  pageType,
  captionHeight,
}) => {
  const slideshowArtworks = artworks
    .filter((image) => image.image.gatsbyImageData !== null)
    .map((image, index) => {
      return (
        <SingleSlide
          className={`section ${index === 0 ? `active` : ``} ${
            image.image.dimensions.width > image.image.dimensions.height
              ? `landscape-image`
              : `portrait-image`
          }`}
          key={`single_slide_${index}_${image.id}`}
          height={staticWindowHeight}
          captionHeight={captionHeight}
        >
          <GalleryImage
            image={image.image.gatsbyImageData}
            alt=""
            index={index}
            pageType={pageType}
          />
        </SingleSlide>
      );
    });

  return slideshowArtworks;
};

export const ArtworksLightbox = ({
  artworks,
  artist,
  setIsLightboxOpen,
  staticWindowHeight,
  isLightboxOpen,
  pageType,
}) => {
  const [currentOuterSlide, setCurrentOuterSlide] = useState(0);
  const [outerFullpageApi, setOuterFullpageApi] = useState(null);
  const [isArtworkEnquiryFormOpen, setIsArtworkEnquiryFormOpen] =
    useState(false);
  const [ref, { x, y, width, height, top, right, bottom, left }] = useMeasure();

  const flattenedArtworks = artworks.flatMap((artwork) => artwork.items || []);
  const hasSingleImage = flattenedArtworks.length === 1;

  // Unified swipe handlers for mobile navigation
  const swipeHandlers = useSwipeable({
    onSwipedUp: () => {
      // Swipe up = scroll down = next artwork
      if (currentOuterSlide < flattenedArtworks.length - 1) {
        outerFullpageApi?.moveSectionDown();
      }
    },
    onSwipedDown: () => {
      // Swipe down = scroll up = previous artwork
      if (currentOuterSlide > 0) {
        outerFullpageApi?.moveSectionUp();
      }
    },
    trackMouse: false,
    preventScrollOnSwipe: true,
    delta: 30, // minimum swipe distance in pixels
    swipeDuration: 500, // maximum time for swipe
    touchEventOptions: { passive: false },
  });

  useEffect(() => {
    if (outerFullpageApi !== null && outerFullpageApi !== undefined) {
      if (hasSingleImage) {
        outerFullpageApi.setAllowScrolling(false);
        outerFullpageApi.setKeyboardScrolling(false);
      } else {
        outerFullpageApi.setAllowScrolling(true);
        outerFullpageApi.setKeyboardScrolling(true);
      }
    }
  }, [hasSingleImage, outerFullpageApi]);

  return (
    <RemoveScroll>
      <Modal
        isOpen={isLightboxOpen}
        portalClassName={`ReactModalPortal-artworks-lightbox`}
        className="lightbox-modal"
        contentLabel={`Artworks Lightbox`}
        shouldFocusAfterRender={false}
      >
        <FullPageContainer minHeight={height}>
          <CloseButton
            type="button"
            onClick={() => {
              outerFullpageApi.destroy("all");
              setIsLightboxOpen(false);
            }}
          >
            <CloseIcon />
          </CloseButton>

          <ReactFullpage
            loopHorizontal={false}
            loopTop={false}
            loopBottom={false}
            resetSliders={true}
            slidesNavigation={false}
            credits={false}
            // licenseKey={"YOUR_KEY_HERE"}
            touchSensitivity={3}
            scrollingSpeed={1000}
            autoScrolling={true}
            // keyboardScrolling={true}
            css3={true}
            continuousVertical={false}
            // Update progress bar after section loads (doesn't interfere with navigation)
            afterLoad={(origin, destination, direction, trigger) => {
              if (destination && typeof destination.index === "number") {
                setCurrentOuterSlide(destination.index);
              }
            }}
            render={({ state, fullpageApi }) => {
              setOuterFullpageApi(fullpageApi);

              return (
                <ReactFullpage.Wrapper>
                  <SlideshowArtworksContent
                    artworks={flattenedArtworks}
                    artist={artist}
                    staticWindowHeight={staticWindowHeight}
                    pageType={pageType}
                    captionHeight={height}
                  />
                </ReactFullpage.Wrapper>
              );
            }}
          />

          {!hasSingleImage && (
            <>
              <PreviousArtworkArrow
                type="button"
                className={currentOuterSlide === 0 ? "hidden" : "active"}
                onClick={() => outerFullpageApi?.moveSectionUp()}
                aria-label="Previous artwork"
              />
              <NextArtworkArrow
                type="button"
                className={
                  currentOuterSlide === flattenedArtworks.length - 1
                    ? "hidden"
                    : "active"
                }
                onClick={() => outerFullpageApi?.moveSectionDown()}
                aria-label="Next artwork"
              />
              <MobileSwipeArea {...swipeHandlers} aria-hidden="true" />
            </>
          )}

          <Caption ref={ref}>
            <div
              className="progress-bar"
              style={{
                width: `${
                  ((currentOuterSlide + 1) / flattenedArtworks.length) * 100
                }%`,
                transition: "width 1000ms ease",
              }}
            />
            <div className="column middle">
              <PrismicRichText
                field={artist.richText}
                components={{
                  heading2: ({ children }) => <p className="">{children}, </p>,
                }}
              />
              <PrismicRichText
                field={
                  flattenedArtworks[currentOuterSlide].image_title.richText
                }
                components={{
                  paragraph: ({ children }) => <p className="">{children}, </p>,
                }}
              />

              <PrismicRichText
                field={flattenedArtworks[currentOuterSlide].caption.richText}
              />
            </div>

            <div className="column right">
              <button
                type="button"
                className="italic"
                onClick={() => setIsArtworkEnquiryFormOpen(true)}
              >
                Enquire
              </button>
            </div>
          </Caption>
        </FullPageContainer>

        {isArtworkEnquiryFormOpen && (
          <ArtworkEnquiryForm
            artist={artist}
            artwork={flattenedArtworks[currentOuterSlide].image_title}
            url={window.location.href}
            setIsArtworkEnquiryFormOpen={setIsArtworkEnquiryFormOpen}
          />
        )}
      </Modal>
    </RemoveScroll>
  );
};
